import { reportingIdentifier } from 'src/queries/reporting/reporting.identifier'
import type {
  ReportingRunEvent,
  ReportingStreamCursor,
} from 'src/queries/reportingAgent/reportingAgent.models'
import { REPORTING_AGENT_VERSION } from 'src/queries/reportingAgent/reportingAgent.models'

import {
  exact,
  object,
  OPERATIONAL_FAILURES,
  PROGRESS_STAGES,
  string,
  version,
} from './primitives'

export const parseReportingStreamCursor = (
  value: unknown,
): ReportingStreamCursor => {
  const source = object(value, 'stream cursor')
  exact(source, ['sequence', 'reconnect'], 'stream cursor')
  if (
    typeof source.sequence !== 'number' ||
    !Number.isInteger(source.sequence) ||
    source.sequence < 0
  ) {
    throw new Error('stream cursor.sequence is invalid')
  }
  if (source.reconnect !== true)
    throw new Error('stream cursor.reconnect must be true')
  return { sequence: source.sequence, reconnect: true }
}

export const parseReportingRunEvent = (value: unknown): ReportingRunEvent => {
  const source = object(value, 'run event')
  exact(
    source,
    ['version', 'type', 'run_id', 'sequence', 'terminal', 'payload'],
    'run event',
  )
  version(source.version, 'run event')
  const types = [
    'run.status',
    'assistant.text.delta',
    'report.progress',
    'report.result.ready',
    'run.completed',
    'run.failed',
    'run.cancelled',
  ]
  if (typeof source.type !== 'string' || !types.includes(source.type))
    throw new Error('run event.type is invalid')
  if (
    typeof source.sequence !== 'number' ||
    !Number.isInteger(source.sequence) ||
    source.sequence < 1
  )
    throw new Error('run event.sequence is invalid')
  if (typeof source.terminal !== 'boolean')
    throw new Error('run event.terminal is invalid')
  const type = source.type as ReportingRunEvent['type']
  const payloadSource = object(source.payload, 'run event.payload')
  let payload: Record<string, unknown>
  let terminal: boolean
  switch (type) {
    case 'run.status':
      exact(payloadSource, ['status'], 'run event.payload')
      if (!['queued', 'running'].includes(String(payloadSource.status)))
        throw new Error('run event.payload.status is invalid')
      payload = { status: payloadSource.status }
      terminal = false
      break
    case 'assistant.text.delta':
      exact(payloadSource, ['delta'], 'run event.payload')
      {
        const delta = string(payloadSource.delta, 'run event.payload.delta')
        if (delta.length === 0 || delta.length > 8192)
          throw new Error('run event.payload.delta exceeds bounds')
        payload = { delta }
      }
      terminal = false
      break
    case 'report.progress':
      exact(payloadSource, ['stage'], 'run event.payload')
      {
        const stage = string(payloadSource.stage, 'run event.payload.stage')
        if (!PROGRESS_STAGES.has(stage))
          throw new Error('run event.payload.stage is invalid')
        payload = { stage }
      }
      terminal = false
      break
    case 'report.result.ready':
      exact(payloadSource, ['artifact_id'], 'run event.payload')
      payload = {
        artifact_id: reportingIdentifier(
          payloadSource.artifact_id,
          'run event.payload.artifact_id',
        ),
      }
      terminal = false
      break
    case 'run.completed':
      exact(payloadSource, [], 'run event.payload')
      payload = {}
      terminal = true
      break
    case 'run.failed': {
      exact(
        payloadSource,
        ['code', 'retryable', 'message'],
        'run event.payload',
      )
      if (payloadSource.code === 'service_unavailable') {
        if (payloadSource.retryable !== true)
          throw new Error('run event.payload.retryable is invalid')
        if (payloadSource.message !== 'The report could not be completed.')
          throw new Error('run event.payload.message is invalid')
        payload = {
          code: 'service_unavailable',
          retryable: true,
          message: 'The report could not be completed.',
        }
      } else if (
        payloadSource.code === 'unsupported_data' ||
        payloadSource.code === 'unsupported_chart' ||
        payloadSource.code === 'clarification_required'
      ) {
        if (payloadSource.retryable !== false)
          throw new Error('run event.payload.retryable is invalid')
        const message = string(
          payloadSource.message,
          'run event.payload.message',
        )
        if (message.length < 1 || message.length > 320)
          throw new Error('run event.payload.message exceeds bounds')
        payload = {
          code: payloadSource.code,
          retryable: false,
          message,
        }
      } else if (
        typeof payloadSource.code === 'string' &&
        payloadSource.code in OPERATIONAL_FAILURES
      ) {
        const code = payloadSource.code as keyof typeof OPERATIONAL_FAILURES
        const expected = OPERATIONAL_FAILURES[code]
        if (payloadSource.retryable !== expected.retryable)
          throw new Error('run event.payload.retryable is invalid')
        if (payloadSource.message !== expected.message)
          throw new Error('run event.payload.message is invalid')
        payload = {
          code,
          retryable: expected.retryable,
          message: expected.message,
        }
      } else {
        throw new Error('run event.payload.code is invalid')
      }
      terminal = true
      break
    }
    case 'run.cancelled':
      exact(payloadSource, ['reason'], 'run event.payload')
      if (payloadSource.reason !== 'user_requested')
        throw new Error('run event.payload.reason is invalid')
      payload = { reason: 'user_requested' }
      terminal = true
      break
    default:
      throw new Error('run event.type is invalid')
  }
  if (source.terminal !== terminal)
    throw new Error('run event.terminal does not match event type')
  const runId = reportingIdentifier(source.run_id, 'run event.run_id')
  return {
    version: REPORTING_AGENT_VERSION,
    type,
    run_id: runId,
    sequence: source.sequence as number,
    terminal,
    payload,
  } as ReportingRunEvent
}
